import React, { useState, useEffect } from 'react';
import { Tag, X, Plus, Loader2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { EditableCell } from './EditableCell';
import { useTiposEmpresa } from '../hooks/useTiposEmpresa';

/**
 * Onda UI filtros_compactos 2026-04-26 — Frente C.
 *
 * Seção "Segmentação" editável na ficha da empresa (HistoryModal).
 * Cada campo grava na empresa + registra evidence em ai_evidence para
 * proveniencia (humano / Apollo / inferencia de email / agent_XYZ).
 *
 * Props:
 *   empresa: { id, papel_negocio, setores, status_operacional, regiao_atuacao, ... }
 *   userEmail: string para audit
 *   onUpdated(updates): callback chamado apos sucesso (refresh do pai)
 */
const STATUS_OPS = [
  { value: 'active', label: 'Ativa' },
  { value: 'inactive', label: 'Inativa' },
  { value: 'liquidated', label: 'Liquidada' },
  { value: 'acquired', label: 'Adquirida' },
  { value: 'archived', label: 'Arquivada' },
];

// Apresentacao de proveniencia: mapeia agent → label+icone
const PROV_ICONS = {
  human_edit:                   { icon: '👤', label: 'Humano' },
  apollo_enrich:                { icon: '🤖', label: 'Apollo' },
  email_domain_inference:       { icon: '📧', label: 'Inferido de email' },
  papel_inference_agent:        { icon: '📧', label: 'Inferido' },
  company_research_briefer_v1:  { icon: '🧠', label: 'Briefer IA' },
  decision_maker_mapper_v1:     { icon: '🧠', label: 'Decision Maker IA' },
};

const ProvenanceBadge = ({ field, empresaId, reloadKey }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('ai_evidence')
        .select('agent, decided_by, created_at, confidence')
        .eq('entity_type', 'company')
        .eq('entity_id', empresaId)
        .eq('field_name', field)
        .order('created_at', { ascending: false })
        .limit(1);
      if (!cancelled) setInfo((data || [])[0] || null);
    })();
    return () => { cancelled = true; };
  }, [field, empresaId, reloadKey]);

  if (!info) return null;
  const meta = PROV_ICONS[info.agent] || { icon: '🧠', label: info.agent };
  const autor = info.agent === 'human_edit'
    ? (info.decided_by || '—').split('@')[0]
    : meta.label;
  const when = info.created_at
    ? new Date(info.created_at).toLocaleDateString('pt-BR')
    : '';
  const conf = info.confidence != null ? ` · conf ${info.confidence}` : '';

  return (
    <span
      className="text-[10px] text-slate-500 bg-slate-100 rounded px-1 py-0.5 flex-shrink-0"
      title={`${meta.label}${info.decided_by ? ' · ' + info.decided_by : ''}${conf}${when ? ' · ' + when : ''}`}
    >
      {meta.icon} {autor}
    </span>
  );
};

const SetoresEditor = ({ value, options, onSave, disabled }) => {
  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const labelOf = (v) => options.find(o => o.value === v)?.label || v;

  const handleRemove = (setor) => {
    onSave((value || []).filter(s => s !== setor));
  };
  const handleAdd = (setor) => {
    if (!setor) return;
    if (!(value || []).includes(setor)) {
      onSave([...(value || []), setor]);
    }
    setAdding(false);
    setNewValue('');
  };

  return (
    <div className="flex flex-wrap gap-1 items-center min-h-[26px]">
      {(value || []).map(s => (
        <span key={s} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[11px]">
          {labelOf(s)}
          {!disabled && (
            <button
              onClick={() => handleRemove(s)}
              className="text-indigo-400 hover:text-indigo-700 ml-0.5"
              title="Remover"
            >
              <X size={10} />
            </button>
          )}
        </span>
      ))}
      {!disabled && (adding ? (
        <select
          value={newValue}
          onChange={(e) => handleAdd(e.target.value)}
          onBlur={() => { setAdding(false); setNewValue(''); }}
          autoFocus
          className="text-[11px] border border-indigo-400 rounded px-1 py-0.5 bg-white"
        >
          <option value="">Selecionar...</option>
          {options
            .filter(o => !(value || []).includes(o.value))
            .map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="px-1 py-0.5 text-[10px] text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded border border-dashed border-slate-300 flex items-center gap-0.5"
        >
          <Plus size={9} /> setor
        </button>
      ))}
      {(value || []).length === 0 && disabled && (
        <span className="text-[11px] text-slate-400 italic">—</span>
      )}
    </div>
  );
};

const SegmentacaoEmpresa = ({ empresa, userEmail, onUpdated }) => {
  const { papeis, setores, loading } = useTiposEmpresa();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reloadProv, setReloadProv] = useState(0);

  if (!empresa?.id) return null;

  const handleUpdate = async (field, newValue) => {
    if (saving) return;
    const oldValue = empresa[field];
    // Igualdade shallow para arrays (evita rewrite desnecessário)
    if (Array.isArray(newValue) && Array.isArray(oldValue)
      && newValue.length === oldValue.length
      && newValue.every((v, i) => v === oldValue[i])) return;
    if (!Array.isArray(newValue) && newValue === oldValue) return;

    setSaving(true);
    setError(null);
    try {
      const { error: upErr } = await supabase
        .from('empresas')
        .update({ [field]: newValue })
        .eq('id', empresa.id);
      if (upErr) throw upErr;

      // Evidence trail (best-effort; falha aqui nao reverte o UPDATE).
      try {
        await supabase.from('ai_evidence').insert({
          entity_type: 'company',
          entity_id: empresa.id,
          agent: 'human_edit',
          field_name: field,
          suggested_value: newValue,
          human_decision: 'approved',
          decided_by: userEmail || 'desconhecido',
          confidence: 1.0,
          reasoning: `Edição manual de ${field} por ${userEmail || 'desconhecido'}`,
          metadata: {
            source: 'human_inline_edit',
            old_value: oldValue,
            edited_at: new Date().toISOString(),
          },
        });
      } catch (evErr) {
        console.warn('[Segmentacao] falha ao gravar evidence trail:', evErr);
      }

      if (onUpdated) onUpdated({ [field]: newValue });
      setReloadProv(r => r + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const papelLabel = papeis.find(p => p.value === empresa.papel_negocio)?.label || empresa.papel_negocio || '—';
  const statusLabel = STATUS_OPS.find(s => s.value === empresa.status_operacional)?.label || empresa.status_operacional || '—';

  return (
    <div className="px-5 py-3 border-b bg-slate-50">
      <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
        <Tag size={12} /> Segmentação
        {saving && <Loader2 size={11} className="animate-spin text-slate-400" />}
      </div>
      {error && (
        <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}
      {loading ? (
        <div className="text-[11px] text-slate-400 italic">Carregando opções...</div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {/* Papel / Tipo */}
          <div className="min-w-[150px]">
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <span>Tipo</span>
              <ProvenanceBadge field="papel_negocio" empresaId={empresa.id} reloadKey={reloadProv} />
            </div>
            <EditableCell
              value={empresa.papel_negocio || ''}
              options={[{ value: '', label: '—' }, ...papeis]}
              onSave={(v) => handleUpdate('papel_negocio', v || null)}
              type="select"
              displayValue={papelLabel}
              className="text-xs"
            />
          </div>

          {/* Setores (array) */}
          <div className="min-w-[240px] flex-grow">
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <span>Setores</span>
              <ProvenanceBadge field="setores" empresaId={empresa.id} reloadKey={reloadProv} />
            </div>
            <SetoresEditor
              value={empresa.setores || []}
              options={setores}
              onSave={(v) => handleUpdate('setores', v)}
              disabled={saving}
            />
          </div>

          {/* Status operacional */}
          <div className="min-w-[130px]">
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <span>Status</span>
              <ProvenanceBadge field="status_operacional" empresaId={empresa.id} reloadKey={reloadProv} />
            </div>
            <EditableCell
              value={empresa.status_operacional || ''}
              options={STATUS_OPS}
              onSave={(v) => handleUpdate('status_operacional', v || null)}
              type="select"
              displayValue={statusLabel}
              className="text-xs"
            />
          </div>

          {/* Região atuação (texto livre; regiao_atuacao e array no banco — usamos o 1o elemento) */}
          <div className="min-w-[150px]">
            <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1">
              <span>Região</span>
              <ProvenanceBadge field="regiao_atuacao" empresaId={empresa.id} reloadKey={reloadProv} />
            </div>
            <EditableCell
              value={Array.isArray(empresa.regiao_atuacao) ? (empresa.regiao_atuacao[0] || '') : (empresa.regiao_atuacao || '')}
              onSave={(v) => {
                const clean = (v || '').trim();
                const arr = clean ? [clean] : [];
                handleUpdate('regiao_atuacao', arr);
              }}
              type="text"
              placeholder="Ex: SP, Interior"
              displayValue={
                Array.isArray(empresa.regiao_atuacao) && empresa.regiao_atuacao.length > 0
                  ? empresa.regiao_atuacao.join(', ')
                  : '—'
              }
              className="text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentacaoEmpresa;
